import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box } from "@mui/material"

import type { EmailRecipient, TargetSelection } from "../email-target-selector"
import { RELATIONSHIP_FACETS, RELATIONSHIP_LABELS, type RelationshipFacet } from "./facets"
import { buildTargetSelection, type FacetState } from "./target-selection"
import {
  fetchRelationshipCounts,
  fetchIndustries,
  fetchContactGroups,
  fetchAudiencePreview,
  fetchPeople,
  type ContactGroupOption,
} from "./audience-api"
import { FacetRail, type IndustryOption } from "./FacetRail"
import { ResultsList } from "./ResultsList"
import { SelectionSummary, buildSourceChips } from "./SelectionSummary"

const PREVIEW_DEBOUNCE_MS = 300

/** Derives the initial UI facet state from an incoming `TargetSelection` — the
 * shell's source of truth is `value`, this just seeds local state on mount /
 * whenever a caller resets `value` out from under us. */
function facetStateFromSelection(value: TargetSelection): FacetState {
  return {
    relationships: value.filters?.relationship ?? [],
    industries: value.filters?.industries ?? [],
    excluded: value.filters?.exclude ?? [],
    individuals: value.recipients ?? [],
    groups: value.groups ?? [],
  }
}

/** Short human summary of the current facet state, for `onSummaryChange`. */
function buildSummary(state: FacetState): string {
  const parts: string[] = []
  if (state.relationships.length > 0) {
    parts.push(state.relationships.map((r) => RELATIONSHIP_LABELS[r]).join(" + "))
  }
  if (state.groups.length > 0) {
    parts.push(`${state.groups.length} contact group${state.groups.length !== 1 ? "s" : ""}`)
  }
  if (state.industries.length > 0) {
    parts.push(`in ${state.industries.join(" + ")}`)
  }
  const individualCount = state.individuals.length
  if (individualCount > 0) {
    parts.push(`${individualCount} individual${individualCount !== 1 ? "s" : ""}`)
  }
  if (state.excluded.length > 0) {
    parts.push(`${state.excluded.length} unchecked`)
  }
  return parts.length > 0 ? parts.join(" · ") : "No audience selected yet"
}

export interface AudienceBuilderProps {
  value: TargetSelection
  onChange: (selection: TargetSelection) => void
  disabled?: boolean
  onSummaryChange?: (summary: string) => void
}

/**
 * Shell of the unified Email Campaigns Audience Builder — one deduplicated
 * People list narrowed by stackable facets (relationship, contact groups,
 * industry), a live server-resolved dedup count, and a "select all, then
 * uncheck" membership model: selecting a relationship/group means ALL its
 * people are IN, and the operator unchecks individuals as exceptions
 * (`excluded`). Drop-in prop-compatible with the old selector so callers
 * don't need to change.
 */
export default function AudienceBuilder({ value, onChange, disabled = false, onSummaryChange }: AudienceBuilderProps) {
  // `value` is the source of truth; this local FacetState is the UI's working
  // copy, re-seeded whenever the parent hands us a materially different value
  // (e.g. a form reset). We track the last selection WE emitted so an
  // onChange echo doesn't cause us to stomp mid-edit local state.
  const [state, setState] = useState<FacetState>(() => facetStateFromSelection(value))
  const lastEmitted = useRef<TargetSelection | null>(null)

  const [relationshipCounts, setRelationshipCounts] = useState<Partial<Record<RelationshipFacet, number>>>({})
  const [groupOptions, setGroupOptions] = useState<ContactGroupOption[]>([])
  const [industryOptions, setIndustryOptions] = useState<IndustryOption[]>([])
  const [people, setPeople] = useState<EmailRecipient[]>([])
  const [peopleTotal, setPeopleTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [peopleLoading, setPeopleLoading] = useState(false)
  const [preview, setPreview] = useState<{ total: number; deduped: number; duplicates: number } | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  // Search box (server-side): a global find across ALL populations so you can
  // locate + add any contact regardless of the current facets. Debounced so
  // typing doesn't spam the endpoint.
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Re-seed local state if the parent hands us a different `value` than what
  // we last emitted ourselves (external reset, e.g. switching campaigns).
  useEffect(() => {
    if (lastEmitted.current === value) return
    setState(facetStateFromSelection(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    fetchRelationshipCounts()
      .then(setRelationshipCounts)
      .catch(() => setRelationshipCounts({}))
  }, [])

  useEffect(() => {
    fetchContactGroups()
      .then(setGroupOptions)
      .catch(() => setGroupOptions([]))
  }, [])

  useEffect(() => {
    fetchIndustries(state.relationships)
      .then(setIndustryOptions)
      .catch(() => setIndustryOptions([]))
  }, [state.relationships])

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [state.relationships, state.industries, state.groups, debouncedSearch])

  useEffect(() => {
    let cancelled = false
    setPeopleLoading(true)
    fetchPeople(state.relationships, state.industries, state.groups, debouncedSearch, page)
      .then((result) => {
        if (cancelled) return
        setPeople((prev) => (page === 1 ? result.people : [...prev, ...result.people]))
        setPeopleTotal(result.total)
      })
      .catch(() => {
        if (cancelled) return
        if (page === 1) setPeople([])
      })
      .finally(() => {
        if (!cancelled) setPeopleLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [state.relationships, state.industries, state.groups, debouncedSearch, page])

  const emit = useCallback(
    (next: FacetState) => {
      setState(next)
      const selection = buildTargetSelection(next)
      lastEmitted.current = selection
      onChange(selection)
      onSummaryChange?.(buildSummary(next))
    },
    [onChange, onSummaryChange],
  )

  // Debounced live dedup preview: every facet/individual/group/exclude change
  // queues a fetchAudiencePreview call ~300ms after the last change, so rapid
  // checkbox toggles don't spam the endpoint.
  useEffect(() => {
    let cancelled = false
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const selection = buildTargetSelection(state)
    const hasAnything =
      state.relationships.length > 0 ||
      state.industries.length > 0 ||
      state.individuals.length > 0 ||
      state.groups.length > 0
    if (!hasAnything) {
      setPreview(null)
      return
    }
    setPreviewLoading(true)
    debounceRef.current = setTimeout(() => {
      fetchAudiencePreview(selection)
        .then((result) => {
          if (cancelled) return
          setPreview(result)
        })
        .catch(() => {
          if (cancelled) return
          setPreview(null)
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false)
        })
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      cancelled = true
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.relationships, state.industries, state.individuals, state.groups, state.excluded])

  const individualEmails = useMemo(
    () => new Set(state.individuals.map((r) => r.email.toLowerCase())),
    [state.individuals],
  )
  const excludedEmails = useMemo(() => new Set(state.excluded), [state.excluded])

  // In BROWSE mode the results list is the current pool — the relationship facets,
  // industries, AND the selected groups' members (the backend lists group members
  // now) — so a listed person is "in the pool" unless explicitly excluded. In
  // SEARCH mode the list is a global find across everyone, so a row counts as
  // selected only when it's an explicit individual add (checking one adds it).
  const isSearchMode = debouncedSearch.trim().length > 0
  const hasPoolFacets =
    state.relationships.length > 0 || state.industries.length > 0 || state.groups.length > 0

  const isSelected = useCallback(
    (recipient: EmailRecipient) => {
      const email = recipient.email.toLowerCase()
      if (individualEmails.has(email)) return true
      if (isSearchMode || !hasPoolFacets) return false
      return !excludedEmails.has(email)
    },
    [individualEmails, excludedEmails, hasPoolFacets, isSearchMode],
  )

  const handleRelationshipToggle = useCallback(
    (facet: RelationshipFacet) => {
      const next = state.relationships.includes(facet)
        ? state.relationships.filter((f) => f !== facet)
        : [...state.relationships, facet]
      emit({ ...state, relationships: next })
    },
    [state, emit],
  )

  const handleSelectAllRelationships = useCallback(
    (selectAll: boolean) => {
      emit({ ...state, relationships: selectAll ? [...RELATIONSHIP_FACETS] : [] })
    },
    [state, emit],
  )

  const handleGroupToggle = useCallback(
    (groupId: number) => {
      const next = state.groups.includes(groupId)
        ? state.groups.filter((id) => id !== groupId)
        : [...state.groups, groupId]
      emit({ ...state, groups: next })
    },
    [state, emit],
  )

  const handleIndustryToggle = useCallback(
    (industry: string) => {
      const next = state.industries.includes(industry)
        ? state.industries.filter((i) => i !== industry)
        : [...state.industries, industry]
      emit({ ...state, industries: next })
    },
    [state, emit],
  )

  const handleAddManual = useCallback(
    (recipient: EmailRecipient) => {
      if (individualEmails.has(recipient.email.toLowerCase())) return
      emit({ ...state, individuals: [...state.individuals, recipient] })
    },
    [state, emit, individualEmails],
  )

  // Toggling a results-list row: a hand-added individual gets removed on uncheck;
  // in SEARCH mode a checked row is a find-and-add (becomes an individual); in
  // browse mode a source-included person toggles in/out of `excluded`; an
  // unaffiliated person (nothing selected) becomes a hand-added individual.
  const handleRowToggle = useCallback(
    (recipient: EmailRecipient) => {
      const email = recipient.email.toLowerCase()

      if (individualEmails.has(email)) {
        emit({ ...state, individuals: state.individuals.filter((r) => r.email.toLowerCase() !== email) })
        return
      }

      if (isSearchMode) {
        emit({ ...state, individuals: [...state.individuals, recipient] })
        return
      }

      if (hasPoolFacets) {
        const next = excludedEmails.has(email)
          ? state.excluded.filter((e) => e !== email)
          : [...state.excluded, email]
        emit({ ...state, excluded: next })
        return
      }

      emit({ ...state, individuals: [...state.individuals, recipient] })
    },
    [state, emit, individualEmails, excludedEmails, hasPoolFacets, isSearchMode],
  )

  const handleLoadMore = useCallback(() => setPage((p) => p + 1), [])

  const handleRemoveRelationship = useCallback(
    (facet: RelationshipFacet) => emit({ ...state, relationships: state.relationships.filter((f) => f !== facet) }),
    [state, emit],
  )
  const handleRemoveGroup = useCallback(
    (groupId: number) => emit({ ...state, groups: state.groups.filter((id) => id !== groupId) }),
    [state, emit],
  )
  const handleRemoveIndustry = useCallback(
    (industry: string) => emit({ ...state, industries: state.industries.filter((i) => i !== industry) }),
    [state, emit],
  )

  const groupNameById = useMemo(() => new Map(groupOptions.map((g) => [g.id, g.name])), [groupOptions])

  const sourceChips = buildSourceChips(
    state.relationships,
    handleRemoveRelationship,
    state.groups,
    groupNameById,
    handleRemoveGroup,
    state.industries,
    handleRemoveIndustry,
  )

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* The live "who will receive" total + selected-source chips sit ABOVE the
          selection window so the operator always sees the running audience first. */}
      <SelectionSummary
        sourceChips={sourceChips}
        individualCount={state.individuals.length}
        excludedCount={state.excluded.length}
        preview={preview}
        previewLoading={previewLoading}
        disabled={disabled}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "280px 1fr" }, gap: 2 }}>
        <FacetRail
          relationships={state.relationships}
          relationshipCounts={relationshipCounts}
          groups={state.groups}
          groupOptions={groupOptions}
          industries={state.industries}
          industryOptions={industryOptions}
          disabled={disabled}
          onRelationshipToggle={handleRelationshipToggle}
          onSelectAllRelationships={handleSelectAllRelationships}
          onGroupToggle={handleGroupToggle}
          onIndustryToggle={handleIndustryToggle}
        />
        <ResultsList
          people={people}
          total={peopleTotal}
          isLoading={peopleLoading}
          isSelected={isSelected}
          disabled={disabled}
          search={search}
          onSearchChange={setSearch}
          onToggle={handleRowToggle}
          onAddManual={handleAddManual}
          onLoadMore={handleLoadMore}
          hasMore={people.length < peopleTotal}
        />
      </Box>
    </Box>
  )
}
